import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import {
  collection, addDoc, updateDoc, doc, serverTimestamp, query, where, getDocs
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FICTION_KEYWORDS, searchKeywords, getCategories } from '../../lib/fictionKeywords';
import {
  Plus, BookOpen, Edit2, Archive, Search, X, Check, Upload, ChevronDown, ChevronUp, Tag,
  ArrowRight, Rocket, Eye
} from 'lucide-react';

const BOOK_STATUSES = {
  upcoming:          { label: 'Upcoming',          colour: 'bg-blue-100 text-blue-700',  desc: 'Book exists but not yet in pre-launch' },
  pre_launch_active: { label: 'Pre-Launch Active', colour: 'bg-green-100 text-green-700', desc: 'ARC campaign period — visible to readers' },
  launched:          { label: 'Launched',           colour: 'bg-teal-100 text-teal-700',   desc: 'Book is live and published' },
  archived:          { label: 'Archived',           colour: 'bg-yellow-100 text-yellow-700', desc: 'Hidden from readers' },
};

export default function AuthorBooksTab() {
  const { authorId } = useAuth();
  const { books, campaigns, subscription, basePath } = useAuthorDashboard();
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Basic fields
  const [title, setTitle] = useState('');
  const [series, setSeries] = useState('');
  const [seriesNumber, setSeriesNumber] = useState('');
  const [description, setDescription] = useState('');
  const [contentWarnings, setContentWarnings] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');

  // Stats
  const [wordCount, setWordCount] = useState('');
  const [pageCount, setPageCount] = useState('');

  // Dates
  const [pubDate, setPubDate] = useState('');
  const [arcSendDate, setArcSendDate] = useState('');
  const [goLiveDate, setGoLiveDate] = useState('');
  const [reviewReleaseDate, setReviewReleaseDate] = useState('');

  // Status
  const [status, setStatus] = useState('upcoming');
  const [reviewsEnabled, setReviewsEnabled] = useState(true);

  // Keywords / tags
  const [keywords, setKeywords] = useState([]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordResults, setKeywordResults] = useState([]);
  const [showBrowse, setShowBrowse] = useState(false);
  const [browseCategory, setBrowseCategory] = useState(null);
  const searchRef = useRef(null);

  const bookLimit = subscription?.bookLimit || 5;
  const activeBooks = books.filter(b => b.status !== 'archived');
  const atLimit = activeBooks.length >= bookLimit;

  // Auto-launch: if a book has a pubDate that's today or past and isn't already launched/archived
  useEffect(() => {
    if (!basePath || books.length === 0) return;
    const today = new Date().toISOString().split('T')[0];
    books.forEach(book => {
      if (
        book.pubDate &&
        book.pubDate <= today &&
        book.status !== 'launched' &&
        book.status !== 'archived'
      ) {
        updateDoc(doc(db, basePath, 'books', book.id), {
          status: 'launched',
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
    });
  }, [books, basePath]);

  // Smart search effect
  useEffect(() => {
    if (!keywordSearch.trim()) { setKeywordResults([]); return; }
    const results = searchKeywords(keywordSearch);
    setKeywordResults(results.filter(r => !keywords.includes(r.keyword)));
  }, [keywordSearch, keywords]);

  function resetForm() {
    setTitle(''); setSeries(''); setSeriesNumber('');
    setDescription(''); setContentWarnings('');
    setCoverFile(null); setCoverPreview('');
    setWordCount(''); setPageCount('');
    setPubDate(''); setArcSendDate(''); setGoLiveDate(''); setReviewReleaseDate('');
    setStatus('upcoming');
    setReviewsEnabled(true);
    setKeywords([]); setKeywordSearch(''); setKeywordResults([]);
    setShowBrowse(false); setBrowseCategory(null);
    setEditingBook(null); setError('');
  }

  function openEdit(book) {
    setTitle(book.title || '');
    setSeries(book.series || '');
    setSeriesNumber(book.seriesNumber || '');
    setDescription(book.description || book.blurb || '');
    setContentWarnings(book.contentWarnings || '');
    setCoverPreview(book.coverUrl || '');
    setWordCount(book.wordCount || '');
    setPageCount(book.pageCount || '');
    setPubDate(book.pubDate || '');
    setArcSendDate(book.arcSendDate || '');
    setGoLiveDate(book.goLiveDate || book.releaseDate || '');
    setReviewReleaseDate(book.reviewReleaseDate || '');
    setStatus(book.status || 'upcoming');
    setReviewsEnabled(book.reviewsEnabled !== false);
    setKeywords(book.keywords || book.fictionKeywords || []);
    setEditingBook(book);
    setShowForm(true);
  }

  async function handleSave() {
    if (!title.trim()) { setError('Title is required'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      let coverUrl = editingBook?.coverUrl || '';
      if (coverFile) {
        const fileRef = storageRef(storage, `authors/${authorId}/books/cover-${Date.now()}`);
        await uploadBytes(fileRef, coverFile);
        coverUrl = await getDownloadURL(fileRef);
      }

      const bookData = {
        title: title.trim(),
        series: series.trim(),
        seriesNumber: seriesNumber ? Number(seriesNumber) : null,
        description: description.trim(),
        blurb: description.trim(),
        contentWarnings: contentWarnings.trim(),
        coverUrl,
        wordCount: wordCount ? Number(wordCount) : null,
        pageCount: pageCount ? Number(pageCount) : null,
        pubDate,
        arcSendDate,
        goLiveDate,
        releaseDate: goLiveDate,
        reviewReleaseDate,
        reviewsEnabled,
        status,
        arcAvailable: status === 'pre_launch_active' || status === 'launched',
        fictionKeywords: keywords,
        updatedAt: serverTimestamp(),
      };

      if (editingBook) {
        // Ensure authorId is always set (backfill for older records)
        bookData.authorId = authorId;
        await updateDoc(doc(db, basePath, 'books', editingBook.id), bookData);

        // If status changed to pre_launch_active, auto-create campaign if none exists
        if (status === 'pre_launch_active' && editingBook.status !== 'pre_launch_active') {
          await ensureCampaignExists(editingBook.id, title.trim(), coverUrl);
        }
      } else {
        bookData.createdAt = serverTimestamp();
        bookData.authorId = authorId;
        const newBookRef = await addDoc(collection(db, basePath, 'books'), bookData);

        // If created directly as pre_launch_active, auto-create campaign
        if (status === 'pre_launch_active') {
          await ensureCampaignExists(newBookRef.id, title.trim(), coverUrl);
        }
      }

      resetForm();
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  // Auto-create a campaign for a book if one doesn't already exist
  async function ensureCampaignExists(bookId, bookTitle, bookCoverUrl) {
    const existing = campaigns.find(c => c.bookId === bookId);
    if (existing) return;

    await addDoc(collection(db, basePath, 'campaigns'), {
      name: bookTitle,
      bookId,
      bookTitle,
      bookCoverUrl: bookCoverUrl || '',
      spotsAvailable: 20,
      spotsRemaining: 20,
      openDate: '',
      closeDate: '',
      requirements: '',
      preferredProfiles: '',
      arcFormat: 'ebook',
      pitch: '',
      genres: [],
      authorId,
      authorName: '',
      status: 'note_interest',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      pendingCount: 0,
      approvedCount: 0,
    });
  }

  async function handleStatusChange(book, newStatus) {
    const updates = {
      status: newStatus,
      arcAvailable: newStatus === 'pre_launch_active' || newStatus === 'launched',
      updatedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, basePath, 'books', book.id), updates);

    // Auto-create campaign when moving to pre_launch_active
    if (newStatus === 'pre_launch_active') {
      await ensureCampaignExists(book.id, book.title, book.coverUrl);
    }
  }

  function handleFileUpload(file) {
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = e => setCoverPreview(e.target.result);
    reader.readAsDataURL(file);
  }

  function addKeyword(kw) {
    if (!keywords.includes(kw)) {
      setKeywords(prev => [...prev, kw]);
    }
    setKeywordSearch('');
    setKeywordResults([]);
    searchRef.current?.focus();
  }

  function removeKeyword(kw) {
    setKeywords(prev => prev.filter(k => k !== kw));
  }

  const categories = getCategories();

  // Get the campaign for a book (if any)
  function getCampaignForBook(bookId) {
    return campaigns.find(c => c.bookId === bookId);
  }

  // Campaign status label
  const CAMPAIGN_STATUS_LABELS = {
    note_interest: 'Note Interest',
    active: 'Active',
    closed: 'Closed',
    live: 'Live',
    draft: 'Draft',
    open: 'Open',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Books</h1>
          <p className="text-sm text-slate-500">
            {activeBooks.length} of {bookLimit} active book slots used
          </p>
        </div>
        <Button
          className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
          onClick={() => { resetForm(); setShowForm(true); }}
          disabled={atLimit}
        >
          <Plus className="h-4 w-4" /> Add Book
        </Button>
      </div>

      {atLimit && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          You've reached your book limit ({bookLimit} active books).
          Archive a book to free up a slot, or contact Narratoria to increase your limit.
        </div>
      )}

      {/* Lifecycle guide */}
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        <span className="font-semibold text-slate-700">Lifecycle:</span>
        <Badge className="border-0 text-[10px] bg-blue-100 text-blue-700">Upcoming</Badge>
        <ArrowRight className="h-3 w-3" />
        <Badge className="border-0 text-[10px] bg-green-100 text-green-700">Pre-Launch Active</Badge>
        <ArrowRight className="h-3 w-3" />
        <Badge className="border-0 text-[10px] bg-teal-100 text-teal-700">Launched</Badge>
      </div>

      {/* Book Grid */}
      {books.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="p-8 text-center">
            <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No books yet. Add your first book to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {books.map(book => {
            const campaign = getCampaignForBook(book.id);
            const statusConfig = BOOK_STATUSES[book.status] || BOOK_STATUSES.upcoming;
            return (
              <Card key={book.id} className="border-slate-200 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex gap-3 p-4">
                    {book.coverUrl ? (
                      <img src={book.coverUrl} alt="" className="h-28 w-20 rounded object-cover shadow-sm" />
                    ) : (
                      <div className="flex h-28 w-20 items-center justify-center rounded bg-slate-100">
                        <BookOpen className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="truncate font-medium text-slate-900">{book.title}</h3>
                      {book.series && (
                        <p className="text-xs text-slate-500">
                          {book.series}{book.seriesNumber ? ` #${book.seriesNumber}` : ''}
                        </p>
                      )}
                      <Badge className={`mt-1 border-0 text-[10px] ${statusConfig.colour}`}>
                        {statusConfig.label}
                      </Badge>
                      {campaign && book.status === 'pre_launch_active' && (
                        <Badge className="ml-1 mt-1 border-0 text-[10px] bg-slate-100 text-slate-600">
                          Campaign: {CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                        </Badge>
                      )}
                      {(book.fictionKeywords || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {book.fictionKeywords.slice(0, 3).map((kw, i) => (
                            <span key={i} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {kw}
                            </span>
                          ))}
                          {book.fictionKeywords.length > 3 && (
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                              +{book.fictionKeywords.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Action buttons — progress forward or archive */}
                  <div className="flex border-t border-slate-100">
                    <button
                      onClick={() => openEdit(book)}
                      className="flex flex-1 items-center justify-center gap-1.5 py-2 text-xs text-slate-600 hover:bg-slate-50"
                    >
                      <Edit2 className="h-3 w-3" /> Edit
                    </button>
                    {book.status === 'upcoming' && (
                      <button
                        onClick={() => handleStatusChange(book, 'pre_launch_active')}
                        className="flex flex-1 items-center justify-center gap-1.5 border-l border-slate-100 py-2 text-xs text-green-600 hover:bg-green-50"
                      >
                        <Rocket className="h-3 w-3" /> Start Pre-Launch
                      </button>
                    )}
                    {book.status === 'pre_launch_active' && (
                      <button
                        onClick={() => handleStatusChange(book, 'launched')}
                        className="flex flex-1 items-center justify-center gap-1.5 border-l border-slate-100 py-2 text-xs text-teal-600 hover:bg-teal-50"
                      >
                        <Rocket className="h-3 w-3" /> Mark as Launched
                      </button>
                    )}
                    {book.status !== 'archived' ? (
                      <button
                        onClick={() => handleStatusChange(book, 'archived')}
                        className="flex flex-1 items-center justify-center gap-1.5 border-l border-slate-100 py-2 text-xs text-slate-600 hover:bg-slate-50"
                      >
                        <Archive className="h-3 w-3" /> Archive
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(book, 'upcoming')}
                        className="flex flex-1 items-center justify-center gap-1.5 border-l border-slate-100 py-2 text-xs text-teal-600 hover:bg-teal-50"
                        disabled={atLimit}
                      >
                        <Check className="h-3 w-3" /> Restore
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Book Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) { resetForm(); setShowForm(false); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
          </DialogHeader>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* LEFT COLUMN — Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Book title" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Series</Label>
                  <Input value={series} onChange={e => setSeries(e.target.value)} placeholder="Series name" />
                </div>
                <div>
                  <Label>Book #</Label>
                  <Input type="number" value={seriesNumber} onChange={e => setSeriesNumber(e.target.value)} placeholder="1" />
                </div>
              </div>

              <div>
                <Label>Cover Image</Label>
                <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 hover:border-slate-300">
                  {coverPreview ? (
                    <img src={coverPreview} alt="Cover" className="h-16 w-11 rounded object-cover" />
                  ) : (
                    <div className="flex h-16 w-11 items-center justify-center rounded bg-slate-200"><BookOpen className="h-4 w-4 text-slate-400" /></div>
                  )}
                  <span className="text-sm text-slate-600"><Upload className="mr-1 inline h-3 w-3" /> Upload cover</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload(e.target.files[0])} />
                </label>
              </div>

              <div>
                <Label>Blurb / Description *</Label>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Book description/blurb that readers will see..."
                  rows={5}
                />
              </div>

              <div>
                <Label>Content Warnings</Label>
                <Textarea
                  value={contentWarnings}
                  onChange={e => setContentWarnings(e.target.value)}
                  placeholder="Violence, sexual content, etc."
                  rows={2}
                />
              </div>
            </div>

            {/* RIGHT COLUMN — Dates, Stats, Status */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Word Count</Label>
                  <Input type="number" value={wordCount} onChange={e => setWordCount(e.target.value)} placeholder="80000" />
                </div>
                <div>
                  <Label>Page Count</Label>
                  <Input type="number" value={pageCount} onChange={e => setPageCount(e.target.value)} placeholder="350" />
                </div>
              </div>

              <div>
                <Label>ARC Send-Out Date</Label>
                <Input type="date" value={arcSendDate} onChange={e => setArcSendDate(e.target.value)} />
              </div>

              <div>
                <Label>Go-Live Date (Release)</Label>
                <Input type="date" value={goLiveDate} onChange={e => setGoLiveDate(e.target.value)} />
              </div>

              <div>
                <Label>Review Release Date</Label>
                <Input type="date" value={reviewReleaseDate} onChange={e => setReviewReleaseDate(e.target.value)} />
              </div>

              <div>
                <Label>Launch Date</Label>
                <Input type="date" value={pubDate} onChange={e => setPubDate(e.target.value)} />
                <p className="text-[11px] text-slate-400 mt-1">Book auto-launches on this date if not already launched</p>
              </div>

              <div>
                <Label>Status</Label>
                <select
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                >
                  <option value="upcoming">Upcoming — not yet in pre-launch</option>
                  <option value="pre_launch_active">Pre-Launch Active — ARC campaign period</option>
                  <option value="launched">Launched — book is live</option>
                </select>
              </div>

              {/* Reviews enabled toggle */}
              <div>
                <Label>Reviews</Label>
                <button
                  type="button"
                  onClick={() => setReviewsEnabled(!reviewsEnabled)}
                  className={`mt-1 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    reviewsEnabled
                      ? 'border-green-200 bg-green-50 text-green-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}
                >
                  <div className={`h-4 w-8 rounded-full transition-colors ${reviewsEnabled ? 'bg-green-500' : 'bg-slate-300'}`}>
                    <div className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${reviewsEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                  </div>
                  {reviewsEnabled ? 'Reviews open' : 'Reviews closed'}
                </button>
                <p className="text-[11px] text-slate-400 mt-1">When off, readers see a "not open yet" message instead of the review form</p>
              </div>
            </div>
          </div>

          {/* KEYWORDS / TAGS — Full Width */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Keywords & Tags</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Search or browse to add keywords — helps readers find your book
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBrowse(!showBrowse)}
                className="text-xs"
              >
                <Tag className="mr-1 h-3 w-3" />
                {showBrowse ? 'Hide' : 'Browse'} Categories
              </Button>
            </div>

            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {keywords.map(kw => (
                  <span key={kw} className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-1 text-xs font-medium text-teal-800">
                    {kw}
                    <button onClick={() => removeKeyword(kw)} className="ml-0.5 hover:text-teal-950">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                ref={searchRef}
                className="pl-9"
                value={keywordSearch}
                onChange={e => setKeywordSearch(e.target.value)}
                placeholder="Start typing to search keywords... e.g. 'enemies', 'fantasy', 'slow burn'"
              />
              {keywordResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {keywordResults.map((result, i) => (
                    <button
                      key={`${result.keyword}-${i}`}
                      onClick={() => addKeyword(result.keyword)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-teal-50 transition-colors"
                    >
                      <span className="text-slate-900">{result.keyword}</span>
                      <span className="text-[10px] text-slate-400">{result.category}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showBrowse && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                <div className="flex flex-wrap gap-1">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setBrowseCategory(browseCategory === cat ? null : cat)}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                        browseCategory === cat
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {browseCategory && (
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {FICTION_KEYWORDS.find(s => s.category === browseCategory)?.keywords.map(kw => {
                        const isSelected = keywords.includes(kw);
                        return (
                          <button
                            key={kw}
                            onClick={() => isSelected ? removeKeyword(kw) : addKeyword(kw)}
                            className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                              isSelected
                                ? 'bg-teal-600 text-white'
                                : 'bg-white text-slate-700 border border-slate-200 hover:border-teal-400 hover:text-teal-700'
                            }`}
                          >
                            {kw}
                            {isSelected && <Check className="ml-1 inline h-3 w-3" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save / Cancel */}
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : editingBook ? 'Update Book' : 'Add Book'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
